import { Antenna, Brain, Target } from "lucide-react";
import { Reveal, RevealItem } from "@/components/site/Reveal";
import { useT } from "@/lib/i18n/I18nProvider";

export function HowItWorks() {
  const { locale } = useT();
  const isBg = locale === "bg";
  const STEPS = [
    {
      n: "01",
      icon: Antenna,
      title: isBg ? "Събиране" : "Collect",
      body: isBg
        ? "Непрекъснат мониторинг на 150 000+ източници, включително печат, телевизия, социални медии, подкасти, политическо покритие и форуми на 25+ езика."
        : "Continuous monitoring across 150,000+ sources including print, broadcast, social media, podcasts, policy coverage, and forums in 25+ languages.",
    },
    {
      n: "02",
      icon: Brain,
      title: isBg ? "Анализ" : "Analyse",
      body: isBg
        ? "Комбинираме нови технологии и експертен анализ, за да откриваме ключови теми, нагласи, тенденции и позиционирането ви спрямо конкурентите."
        : "Human-validated sentiment, custom topic and entity models, message resonance and competitor benchmarking.",
    },
    {
      n: "03",
      icon: Target,
      title: isBg ? "Действие" : "Act",
      body: isBg
        ? "Получавате сигнали в реално време, регулярни анализи и експертни препоръки, които ви помагат да вземате навременни решения."
        : "Real-time alerts, weekly briefings and a dedicated strategist who turns signal into the next move for your brand.",
    },
  ];
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full opacity-30 blur-[140px]"
        style={{ background: "radial-gradient(circle, #6B5BF5 0%, transparent 70%)" }}
      />
      <div className="noise-overlay" />
      <div className="relative mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <Reveal className="mb-14 max-w-3xl">
          <h2 className="font-display text-[clamp(2rem,3.6vw,3rem)] leading-[1.08] text-white">
            {isBg ? "От сигнал към действие в " : "From signal to action in "}
            <span className="text-gradient-brand">{isBg ? "три стъпки" : "three steps"}</span>
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-white/65">
            {isBg
              ? "Perceptica помага на комуникационните екипи да откриват важните сигнали, да ги анализират и да реагират навреме."
              : "Perceptica gives teams a structured approach to understanding and responding to media activity."}
          </p>
        </Reveal>

        <Reveal stagger className="relative grid gap-6 lg:grid-cols-3">
          {/* Connector */}
          <span
            aria-hidden
            className="pointer-events-none absolute left-0 right-0 top-24 hidden h-px bg-gradient-to-r from-transparent via-white/20 to-transparent lg:block"
          />
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <RevealItem key={s.n}>
                <article className="hover-lift gradient-border group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white/[0.03] p-8 backdrop-blur-sm">
                  <span className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-white/[0.04] to-transparent" />
                  <div className="relative flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-[8px] bg-gradient-brand text-white shadow-[0_8px_24px_-8px_rgba(255,45,120,0.55)]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-display text-[13px] font-semibold tracking-[0.18em] text-accent">
                      {s.n}
                    </span>
                  </div>
                  <h3 className="font-display relative mt-6 text-xl tracking-[-0.02em] text-white">{s.title}</h3>
                  <p className="relative mt-3 text-[13.5px] leading-relaxed text-white/65">{s.body}</p>
                </article>
              </RevealItem>
            );
          })}
        </Reveal>
      </div>
    </section>
  );
}

/**
 * Layout-matched fallback for <HowItWorks />.
 * Same section padding, container width, header block, and 3-card grid
 * so swapping the lazy chunk in causes zero CLS.
 */
export function HowItWorksSkeleton() {
  return (
    <section
      aria-hidden
      className="relative overflow-hidden bg-primary text-primary-foreground"
    >
      <div className="relative mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="mb-14 max-w-3xl space-y-4">
          <div className="h-9 w-3/4 animate-pulse rounded-md bg-white/5 lg:h-12" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-white/5" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="flex h-[260px] flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-8"
            >
              <div className="flex items-center justify-between">
                <div className="h-10 w-10 animate-pulse rounded-[8px] bg-white/10" />
                <div className="h-3 w-8 animate-pulse rounded bg-white/5" />
              </div>
              <div className="mt-6 h-5 w-24 animate-pulse rounded bg-white/10" />
              <div className="mt-3 space-y-2">
                <div className="h-3 w-full animate-pulse rounded bg-white/5" />
                <div className="h-3 w-11/12 animate-pulse rounded bg-white/5" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-white/5" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}